import { useTenantBranding } from '@/branding/TenantBrandingProvider';
import { cn } from '@/lib/utils';

/**
 * `<BrandLogo />` — renders the Sustevo logo.
 *
 * **Variants (W75-F4-S1, handbook § 2.1).** Six approved logos shipped from
 * `Sustevo-Core/sustevo-handbook` `brand/logo/`, vendored byte-for-byte at
 * `/brand/sustevo-<variant>.svg` (the canonical SustEvo asset path —
 * matches `Sustevo-Core/project-management-tool` and any other SustEvo
 * SaaS, so the same URL resolves to the same SVG everywhere):
 *
 *   - `color-full`   — full lockup, brand gradient (default)
 *   - `color-mark`   — leaf-only mark, brand gradient (favicon, collapsed shells)
 *   - `black-full`   — full lockup, single-colour black (printed light bg)
 *   - `black-mark`   — leaf-only mark, single-colour black
 *   - `white-full`   — full lockup, single-colour white (dark / gradient bg)
 *   - `white-mark`   — leaf-only mark, single-colour white
 *
 * **Backwards-compat shorthand.** Callers may still pass `variant="full"` /
 * `variant="mark"`, which map to `color-full` / `color-mark` respectively.
 * This keeps the existing Topbar + Sidebar callsites valid.
 *
 * **Theme-keyed swap (APP_SHELL.md § 3, 2.0.0).** Pass `themed` to render the
 * brand-band lockup that swaps with the active theme: both the `color-full`
 * (light) and `white-full` (dark) SVGs are emitted, and the `.brand-logo-light`
 * / `.brand-logo-dark` rules in `globals.css` reveal exactly one per theme —
 * a CSS-only swap, no JS. `variant` is ignored in this mode. A partner
 * override still wins and renders its single logo regardless of theme.
 *
 * **Min-size (handbook § 2.3).** A `console.warn` fires when `height < 24` for
 * any `*-full` variant or `height < 16` for any `*-mark` variant. The warning
 * is informational — the logo still renders.
 *
 * **Partner branding (W5-F1, W22-F4).** When `tenant_branding.logo_url` is
 * set, that override wins and the `variant` selection is ignored. Falling
 * back to the default-Sustevo SVG when no override is configured is the
 * change vs the previous inline-SVG default.
 */

export type BrandLogoVariant =
  | 'color-full'
  | 'color-mark'
  | 'black-full'
  | 'black-mark'
  | 'white-full'
  | 'white-mark';

/**
 * Backwards-compat shorthand kept so existing `<BrandLogo variant="full" />`
 * / `variant="mark"` callsites in the Topbar + Sidebar don't have to churn.
 */
export type BrandLogoLegacyVariant = 'full' | 'mark';

export interface BrandLogoProps {
  className?: string;
  /**
   * Approved logo variant. Defaults to `'color-full'` (handbook § 2.1).
   * Accepts the legacy `'full'` / `'mark'` shorthand which maps to
   * `color-full` / `color-mark`.
   */
  variant?: BrandLogoVariant | BrandLogoLegacyVariant;
  /**
   * Pixel height for sizing. Defaults to `32` (chrome). Used both as the
   * `<img>` height attribute and to enforce the handbook § 2.3 min-size
   * check (≥ 24px for full, ≥ 16px for mark — `console.warn` when violated).
   */
  height?: number;
  /**
   * Theme-keyed brand-band rendering (APP_SHELL.md § 3, 2.0.0). When `true`,
   * emit both the `color-full` (light) and `white-full` (dark) lockups and let
   * the `.brand-logo-light` / `.brand-logo-dark` rules in `globals.css` show
   * one per active theme. `variant` is ignored. A partner override still wins
   * and renders its single logo for both themes.
   */
  themed?: boolean;
}

const LEGACY_VARIANT_MAP: Record<BrandLogoLegacyVariant, BrandLogoVariant> = {
  full: 'color-full',
  mark: 'color-mark',
};

function resolveVariant(
  v: BrandLogoVariant | BrandLogoLegacyVariant,
): BrandLogoVariant {
  if (v === 'full' || v === 'mark') {
    return LEGACY_VARIANT_MAP[v];
  }
  return v;
}

function checkMinSize(variant: BrandLogoVariant, height: number): void {
  const isMark = variant.endsWith('-mark');
  const min = isMark ? 16 : 24;
  if (height < min) {
    console.warn(
      `[BrandLogo] height ${height}px is below the handbook § 2.3 minimum (${min}px) for variant "${variant}".`,
    );
  }
}

/** Render one logo `<img>`. Shared by the variant + theme-keyed paths so the
 *  sizing, partner-override testid and `object-contain` chrome stay identical. */
function LogoImg({
  src,
  alt,
  height,
  className,
  testId,
  variant,
}: {
  src: string;
  alt: string;
  height: number;
  className?: string;
  testId: string;
  variant: BrandLogoVariant;
}): JSX.Element {
  return (
    <img
      src={src}
      alt={alt}
      height={height}
      style={{ height: `${height}px` }}
      className={cn('shrink-0 object-contain w-auto', className)}
      data-testid={testId}
      data-variant={variant}
    />
  );
}

export function BrandLogo({
  className,
  variant = 'color-full',
  height = 32,
  themed = false,
}: BrandLogoProps): JSX.Element {
  const { branding } = useTenantBranding();
  const alt = `${branding.product_name} logo`;
  const isPartnerOverride = branding.logo_url != null;

  // Theme-keyed band lockup (APP_SHELL.md § 3, 2.0.0). Emit both the light
  // (`color-full`) and dark (`white-full`) SVGs; `.brand-logo-light` /
  // `.brand-logo-dark` in globals.css reveal one per active theme. A partner
  // override is a single logo and renders for both themes (the partner picks
  // a theme-agnostic asset), so it short-circuits the dual render.
  if (themed && !isPartnerOverride) {
    checkMinSize('color-full', height);
    return (
      <>
        <LogoImg
          src="/brand/sustevo-color-full.svg"
          alt={alt}
          height={height}
          className={cn('brand-logo-light', className)}
          testId="brand-logo-color-full"
          variant="color-full"
        />
        <LogoImg
          src="/brand/sustevo-white-full.svg"
          alt={alt}
          height={height}
          className={cn('brand-logo-dark', className)}
          testId="brand-logo-white-full"
          variant="white-full"
        />
      </>
    );
  }

  const resolved = resolveVariant(variant);
  checkMinSize(resolved, height);

  // Partner override (W5-F1 / W22-F4) wins when present. Falls back to the
  // approved Sustevo SVG so the app shell always renders a brand mark.
  const src = branding.logo_url ?? `/brand/sustevo-${resolved}.svg`;

  return (
    <LogoImg
      src={src}
      alt={alt}
      height={height}
      className={className}
      testId={
        isPartnerOverride
          ? `brand-logo-partner-${resolved.endsWith('-mark') ? 'mark' : 'full'}`
          : `brand-logo-${resolved}`
      }
      variant={resolved}
    />
  );
}
